import { convert } from "xmlbuilder2";

import generate_id from "../constants/id-generator";
import { ACCOUNT_ID_PREFIX } from "../constants/debt-info";
import { INCLUDE_IN_DTI } from "../constants/account_dispositions";
import { default_debt_info_account } from "../schema/debt_info";
import {
  INSTALLMENT,
  MORTGAGE,
  HOME_EQUITY,
  OTHER_LIABILITIES,
  REVOLVING_CHARGE,
  STUDENT_LOAN
} from "../constants/liability_types";

// MeridianLink SmartAPI Consumer Credit RESPONSE uses the native bureau format
// (CREDIT_RESPONSE / CREDIT_LIABILITY with CreditLiability* fields + an explicit
// CreditLiabilityAccountOwnershipType), NOT the DU-wrapper <LIABILITY> our
// fromMismoXml importer reads. This parses that native response into the same
// accountSummaries shape the Debt-page importer already consumes.

// Native CreditLiabilityAccountType / CreditLoanType -> LANIS liability type.
const NATIVE_LIABILITY_TYPE = {
  Mortgage: MORTGAGE,
  MortgageLoan: MORTGAGE,
  Revolving: REVOLVING_CHARGE,
  Installment: INSTALLMENT,
  HomeEquity: HOME_EQUITY,
  Educational: STUDENT_LOAN,
  Open: OTHER_LIABILITIES
};

function arr(node) {
  if (node === undefined || node === null) return [];
  return Array.isArray(node) ? node : [node];
}

function money(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function text(value) {
  return value === undefined || value === null ? "" : String(value);
}

/**
 * Parse a native MeridianLink credit response into { document: { accountSummaries } }
 * so it can flow through the same preview/import path as an uploaded file.
 * IDs are placeholders; the Debt-page import re-keys them on merge.
 */
export function fromCreditResponseXml(xml, options) {
  const obj = convert(xml, { format: "object" });

  // The borrower indices this pull is FOR (the walled group the LO selected on
  // the Request Credit Report screen). Imported tradelines are owned by these
  // borrowers ONLY - never the whole file - so one couple's pulled credit can
  // never appear on another borrower's application (privacy wall). Defaults to
  // the primary borrower for older single-borrower callers.
  const ownerIndices =
    options && Array.isArray(options.ownerIndices) && options.ownerIndices.length
      ? options.ownerIndices.map(String)
      : null;

  let deal;
  try {
    deal = obj.MESSAGE.DEAL_SETS.DEAL_SET.DEALS.DEAL;
  } catch (e) {
    deal = undefined;
  }
  if (!deal) return { document: { accountSummaries: {} } };

  const liabilities = [];
  arr(deal.SERVICES && deal.SERVICES.SERVICE).forEach(service => {
    arr(service && service.CREDIT).forEach(credit => {
      arr(credit && credit.CREDIT_RESPONSE).forEach(resp => {
        arr(
          resp &&
            resp.CREDIT_LIABILITIES &&
            resp.CREDIT_LIABILITIES.CREDIT_LIABILITY
        ).forEach(cl => liabilities.push(cl));
      });
    });
  });

  const accountSummaries = {};
  liabilities.forEach((cl, index) => {
    const detail = cl.CREDIT_LIABILITY_DETAIL || {};
    const creditor =
      (cl.CREDIT_LIABILITY_CREDITOR &&
        cl.CREDIT_LIABILITY_CREDITOR.NAME &&
        cl.CREDIT_LIABILITY_CREDITOR.NAME.FullName) ||
      "";

    const nativeType =
      detail.CreditLiabilityAccountType || detail.CreditLoanType;
    const liabilityType = NATIVE_LIABILITY_TYPE[nativeType] || OTHER_LIABILITIES;

    // Ownership: the bureau states it explicitly. A joint tradeline belongs to
    // every borrower in the pulled group; any individual/authorized-user form
    // belongs to that group's primary borrower (the LO can re-assign). When no
    // pull group was passed (legacy single-borrower path) fall back to the old
    // 0 / 0,1 default.
    const ownership = detail.CreditLiabilityAccountOwnershipType;
    const isJoint = ownership === "JointContractualLiability";
    const owners = ownerIndices
      ? (isJoint ? ownerIndices.slice() : [ownerIndices[0]])
      : (isJoint ? ["0", "1"] : ["0"]);

    // High credit / limit: prefer the revolving credit limit, else high balance.
    const highCredit =
      detail.CreditLiabilityCreditLimitAmount ||
      detail.CreditLiabilityHighBalanceAmount ||
      "0";

    const id = `credit-${index}`;
    accountSummaries[id] = {
      ...default_debt_info_account,
      id,
      owners,
      liability_type: liabilityType,
      description: text(creditor),
      account_number: text(detail.CreditLiabilityAccountIdentifier),
      monthly_payment: money(detail.CreditLiabilityMonthlyPaymentAmount),
      current_balance: money(detail.CreditLiabilityUnpaidBalanceAmount),
      high_credit_limit: money(highCredit)
    };
  });

  return { document: { accountSummaries } };
}

function firstMatch(xml, re) {
  const m = re.exec(xml);
  return m ? m[1] : "";
}

/**
 * Pull the async-order status out of a response without a full parse. The order
 * is asynchronous: Submit returns a VendorOrderIdentifier + status "New / NOT
 * READY", then StatusQuery is polled until "Completed / READY".
 * Returns { orderId, statusCode, statusDescription, errorText }.
 */
export function parseOrderStatus(xml) {
  const src = String(xml || "");
  return {
    orderId: firstMatch(src, /<VendorOrderIdentifier>([^<]+)<\/VendorOrderIdentifier>/),
    statusCode: firstMatch(src, /<StatusCode>([^<]*)<\/StatusCode>/),
    statusDescription: firstMatch(
      src,
      /<StatusDescription>([^<]*)<\/StatusDescription>/
    ),
    errorText: firstMatch(src, /<ErrorMessageText>([^<]*)<\/ErrorMessageText>/)
  };
}

/** True once the vendor reports the order is finished. */
export function isOrderReady(status) {
  const code = (status.statusCode || "").toLowerCase();
  const desc = (status.statusDescription || "").toLowerCase();
  return code === "completed" || desc === "ready";
}

/**
 * Extract the printable report documents the vendor embeds in the response:
 * an HTML view (for print) and a Base64 PDF (for save). Each lives in a
 * FOREIGN_OBJECT with an EmbeddedContentXML payload + a MIMETypeIdentifier.
 * Returns { html, pdfBase64 } (either may be "").
 */
export function extractReportDocuments(xml) {
  const src = String(xml || "");
  const re = /<EmbeddedContentXML>([\s\S]*?)<\/EmbeddedContentXML>[\s\S]*?<MIMETypeIdentifier>([^<]+)<\/MIMETypeIdentifier>/g;
  let html = "";
  let pdfBase64 = "";
  let m;
  while ((m = re.exec(src)) !== null) {
    const payload = m[1];
    const mime = (m[2] || "").toLowerCase();
    if (mime.indexOf("pdf") !== -1) {
      pdfBase64 = payload.replace(/\s+/g, "");
    } else if (mime.indexOf("html") !== -1) {
      // Strip a CDATA wrapper if present.
      const cdata = /<!\[CDATA\[([\s\S]*?)\]\]>/.exec(payload);
      html = cdata ? cdata[1] : payload;
    }
  }
  return { html, pdfBase64 };
}

function keyOf(account) {
  return `${String(account.account_number || "").trim()}|${String(
    account.description || ""
  ).trim()}`;
}

/**
 * Merge imported credit tradelines into the document's Debt page, deduping
 * against existing debts on account number + creditor and defaulting each new
 * account to INCLUDE_IN_DTI on every loan. Returns a new document.
 */
export function mergeCreditAccounts(document, accounts) {
  const updated = { ...document.accountSummaries };
  const existingKeys = new Set(Object.values(updated).map(keyOf));
  const updatedLoans = document.loans.map(loan => ({
    ...loan,
    account_dispositions: { ...loan.account_dispositions }
  }));

  accounts.forEach(account => {
    const key = keyOf(account);
    if (account.account_number && existingKeys.has(key)) return;
    const id = generate_id(ACCOUNT_ID_PREFIX, true, Object.keys(updated).length);
    updated[id] = { ...account, id };
    existingKeys.add(key);
    updatedLoans.forEach(loan => {
      loan.account_dispositions[id] = INCLUDE_IN_DTI;
    });
  });

  return { ...document, accountSummaries: updated, loans: updatedLoans };
}
